import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Warning, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import { t } from "@/lib/i18n";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [packageName, setPackageName] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 8) { if (!cancelled) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") { setPackageName(data.package_name || ""); setState("success"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white border border-gray-200 rounded-md p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <CircleNotch size={44} className="text-blue-600 mx-auto mb-4 animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">{t("pay.confirming", "Confirming your payment…")}</h1>
            <p data-testid="payment-checking" className="text-sm text-gray-500">{t("pay.hold_tight", "Hold tight — we're verifying with Stripe. Do not close this page.")}</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle size={48} weight="fill" className="text-green-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">{t("pay.success", "Payment successful!")}</h1>
            <p data-testid="payment-confirmed" className="text-sm text-gray-600 mb-6">
              {packageName ? `Your ${packageName} plan is now active.` : "Your plan is now active."} Your team seats and agent features are unlocked.
            </p>
            <button data-testid="goto-inbox-btn" onClick={() => navigate("/app")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md py-3 text-sm transition-colors">{t("pay.go_inbox", "Go to Inbox")}</button>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <Warning size={44} weight="fill" className="text-amber-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">{state === "timeout" ? "Still processing" : "Payment not confirmed"}</h1>
            <p data-testid="payment-error" className="text-sm text-gray-600 mb-6">
              {state === "timeout" ? "Payment is taking longer than expected. Your plan will activate automatically once confirmed." : "The payment session expired or failed. You have not been charged."}
            </p>
            <button data-testid="back-to-pricing-btn" onClick={() => navigate("/")}
              className="w-full border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium rounded-md py-3 text-sm transition-colors">{t("pay.back_pricing", "Back to pricing")}</button>
          </>
        )}
      </div>
    </div>
  );
}
